import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartFooterBlockConfig } from '@front-platform/content-builder';
import { kioskCartFooterBlockResolveConfig } from '@front-platform/content-builder';
import { Component, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { Paths } from '@config/paths.config';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { formatLegalCurrencyAmount } from '@core/services/display-currency/convert-display-currency.util';
import { DisplayCurrencyService } from '@core/services/display-currency/display-currency.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { combineLatest, map } from 'rxjs';
import { AnalyticsService } from '@core/services/app/analytics.service';
export interface KioskCartFooterBlockRuntimeConfig {}

export type KioskCartFooterBlockConfigInput = Partial<
  Omit<KioskCartFooterBlockConfig, keyof KioskCartFooterBlockRuntimeConfig> &
    KioskCartFooterBlockRuntimeConfig
>;
export type KioskCartFooterBlockResolvedConfig = Omit<
  KioskCartFooterBlockConfig,
  keyof KioskCartFooterBlockRuntimeConfig
> &
  Partial<KioskCartFooterBlockRuntimeConfig>;
export interface KioskCartFooterBlockContract extends ConfigComponentContract<
  KioskCartFooterBlockConfigInput,
  KioskCartFooterBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-footer-block',
  templateUrl: './kiosk-cart-footer-block.component.html',
  standalone: false,
})
export class KioskCartFooterBlockComponent implements KioskCartFooterBlockContract {
  readonly config = input<KioskCartFooterBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartFooterBlockConfig,
    KioskCartFooterBlockRuntimeConfig
  >(kioskCartFooterBlockResolveConfig, () => ({ config: this.config() }));
  get primaryButtonBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'primary_button_background_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get primaryButtonTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'primary_button_text_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get secondaryButtonBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'secondary_button_background_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get secondaryButtonTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'secondary_button_text_color'
    ] as unknown as IContentBuilderFieldColor;
  }

  protected readonly displayCurrency = inject(DisplayCurrencyService);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);

  isNavigationToCartInProgress = false;

  order$ = this.orderSelector.selectValidation$;
  cartProducts$ = this.cartRefiner.selectActiveCartProducts;

  isValidOrder$ = combineLatest({
    orderProducts: this.cartRefiner.selectActiveCartProducts,
    orderErrors: this.orderSelector.selectErrors,
  }).pipe(
    map(({ orderProducts, orderErrors }) => {
      return orderProducts.length > 0 && !orderErrors.violations.length;
    }),
  );

  constructor(
    private readonly orderSelector: OrderStoreSelector,
    private readonly cartRefiner: CartStoreRefiner,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly analyticsService: AnalyticsService,
  ) {}

  navigateToFunnel(): void {
    this.isNavigationToCartInProgress = true;
    this.router.navigate([this.routerHelper.translateRoute(`/${Paths.Cart}/${Paths.Funnel}`)]);
  }

  back(): void {
    this.navigationHistoryService.goBack();
  }

  legalPayAmount(finalPrice: number | undefined): string {
    const currency = this.marketplaceSelector.selectMarketplace().currency;
    return formatLegalCurrencyAmount((finalPrice ?? 0) / 100, currency);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartFooterBlockComponent, { 'keyAliases': {} });
